import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAllDocuments, createDocument, deleteDocument, getAllEngagements, getAllBrands } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import Header from '../../components/layout/Header';
import Modal from '../../components/ui/Modal';
import DocumentForm from '../../components/forms/DocumentForm';
import { DOCUMENT_CATEGORY, DOCUMENT_VISIBILITY, formatDate, label, options } from '../../constants/garmentCodes';

const TYPE_COLORS = {
  PDF: 'bg-red-100 text-red-700',
  JPG: 'bg-blue-100 text-blue-700',
  JPEG: 'bg-blue-100 text-blue-700',
  PNG: 'bg-blue-100 text-blue-700',
  XLSX: 'bg-green-100 text-green-700',
};

export default function Documents() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const engagementId = params.get('engagementId') || '';
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  const documents = useSelector((state) => state.document.documents);
  const loading = useSelector((state) => state.document.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const brands = useSelector((state) => state.brand.brands);
  const userId = useSelector((state) => state.auth?.loginUser?.userId ?? null);

  useEffect(() => {
    dispatch(getAllEngagements());
    dispatch(getAllBrands());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllDocuments({ engagementId, brandId: brandFilter, category: categoryFilter }));
  }, [dispatch, engagementId, brandFilter, categoryFilter]);

  const create = (body) => {
    dispatch(createDocument({ payload: body }));
    setUploadOpen(false);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Header title="Documents" breadcrumbs={[{ workspace: true }, { label: 'Documents' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="text-[12px] border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
            <option value="">All categories</option>
            {options(DOCUMENT_CATEGORY).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="text-[12px] border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
            <option value="">All brands</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
          <button onClick={() => setUploadOpen(true)} className="ml-auto px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
            ↑ Upload
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Documents"
            columns={[
              { id: 'fileName', label: 'FILE NAME', align: 'left' },
              { id: 'category', label: 'CATEGORY', align: 'left' },
              { id: 'linkedTo', label: 'LINKED TO', align: 'left' },
              { id: 'brand', label: 'BRAND', align: 'left' },
              { id: 'uploadedBy', label: 'UPLOADED BY', align: 'left' },
              { id: 'date', label: 'DATE', align: 'left' },
              { id: 'size', label: 'SIZE', align: 'right' },
              { id: 'visibility', label: 'VISIBILITY', align: 'left' },
              { id: 'actions', label: 'ACTIONS', align: 'left', disableFilter: true },
            ]}
            rows={documents.map((doc) => {
              const type = doc.fileType || '';
              const linked = doc.linkedItemType ? `${doc.linkedItemType} #${doc.linkedItemId ?? ''}` : '—';
              const visibility = label(DOCUMENT_VISIBILITY, doc.visibility);
              return {
                id: doc.id,
                rColumns: [
                  <div key="name" label={doc.name || ''} className={`flex items-center gap-2 w-full h-full px-2 py-1 ${doc.__optimistic ? 'opacity-60' : ''}`}>
                    {type && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${TYPE_COLORS[type.toUpperCase()] ?? 'bg-gray-100 text-gray-600'}`}>{type.toUpperCase()}</span>}
                    <span className="text-[12px] text-gray-800">{doc.name}</span>
                  </div>,
                  <div key="cat" label={label(DOCUMENT_CATEGORY, doc.category)} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600">{label(DOCUMENT_CATEGORY, doc.category)}</div>,
                  <div key="link" label={linked} className="flex items-center w-full h-full px-2 py-1 font-mono text-[11px] text-[#1b3a6b]">{linked}</div>,
                  <div key="brand" label={doc.brandName || ''} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-700">{doc.brandName || '—'}</div>,
                  <div key="upby" label={doc.uploadedBy || ''} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600">{doc.uploadedBy || '—'}</div>,
                  <div key="date" label={doc.uploadedDate || ''} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-500">{formatDate(doc.uploadedDate)}</div>,
                  <div key="size" label={doc.size || ''} className="flex items-center justify-end w-full h-full px-2 py-1 text-[12px] text-gray-500">{doc.size || '—'}</div>,
                  <div key="vis" label={visibility} className="flex items-center w-full h-full px-2 py-1">
                    <span className={`text-[11px] font-medium ${doc.visibility === 1 ? 'text-blue-600' : 'text-gray-500'}`}>{visibility}</span>
                  </div>,
                  <div key="act" label="" className="flex items-center gap-3 w-full h-full px-2 py-1">
                    {doc.fileUrl && <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:underline">Open</a>}
                    {!doc.__optimistic && <button onClick={() => dispatch(deleteDocument({ id: doc.id }))} className="text-[11px] text-gray-400 hover:text-red-600">Delete</button>}
                  </div>,
                ],
              };
            })}
          />
          {!loading && documents.length === 0 && <p className="py-10 text-center text-[12px] text-gray-400">No documents</p>}
        </div>
      </main>

      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload Document" size="md">
        <DocumentForm engagements={engagements} userId={userId} onSubmit={create} onCancel={() => setUploadOpen(false)} />
      </Modal>
    </div>
  );
}
